import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useDebouncedValue } from "../useDebouncedValue";
import { ColumnTypeMap, detectColumnTypes } from "./cellValue";
import { GridSelection, selectionAfterColumnsMoved } from "./gridGeometry";
import { measureColumnWidths } from "./gridGeometry";
import { buildSheetSource, buildView, detectHeaderRow } from "./sheetPipeline";
import {
  countMatchesOutsideView,
  findMatches,
  MatchAnchor,
  resolveActiveIndex,
  SheetMatch,
  toAnchor,
} from "./sheetSearch";
import { SheetExportPart } from "./sheetExport";
import { CompiledView, RawSheet, SheetSource } from "./sheetTypes";
import {
  emptySheetViewState,
  isSheetViewModified,
  isSheetViewWorthPersisting,
  SheetViewAction,
  SheetViewState,
  WorkbookViewState,
} from "./sheetViewState";
import { loadSheetView, saveSheetView } from "./sheetViewStorage";
import {
  canRedo as historyCanRedo,
  canUndo as historyCanUndo,
  initialViewHistory,
  viewHistoryReducer,
} from "./viewHistory";

/**
 * Everything the spreadsheet UI needs, assembled from the pure modules: the
 * compiled view, the search over it, the selection, and the plumbing that
 * remembers a view between openings.
 */

const SEARCH_DEBOUNCE_MS = 200;
const PERSIST_DEBOUNCE_MS = 500;

/** A sheet the renderer can produce on demand - xlsx converts lazily. */
export interface SheetLoader {
  id: string;
  name: string;
  load: () => RawSheet;
}

export interface RestoredViewInfo {
  filters: number;
  sorts: number;
  hiddenColumns: number;
  hiddenRows: number;
  /**
   * A flag rather than a count: moving one column shifts every column it passed,
   * so any number would be a number the person did not do.
   */
  columnsReordered: boolean;
}

export interface SheetViewApi {
  sheetIds: string[];
  sheetNames: string[];
  activeSheetId: string;
  setActiveSheet: (sheetId: string) => void;

  raw: RawSheet;
  source: SheetSource;
  view: CompiledView;
  types: ColumnTypeMap;
  state: SheetViewState;
  widthBySourceIndex: number[];
  defaultHeaderRowIndex: number | null;
  isModified: boolean;

  query: string;
  setQuery: (value: string) => void;
  matches: SheetMatch[];
  matchesTruncated: boolean;
  activeMatchIndex: number;
  activeMatch: SheetMatch | null;
  gotoNextMatch: () => void;
  gotoPrevMatch: () => void;
  matchesOutsideView: { rows: number; hiddenColumnRows: number };

  selection: GridSelection | null;
  setSelection: (selection: GridSelection | null) => void;

  buildAllSheetExports: () => SheetExportPart[];
  dispatch: (action: SheetViewAction) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  restored: RestoredViewInfo | null;
  dismissRestored: () => void;
  resetView: () => void;
}

function describeRestored(state: SheetViewState): RestoredViewInfo {
  let filters = 0;
  const ids = Object.keys(state.filters);
  for (let i = 0; i < ids.length; i++) {
    const filter = state.filters[ids[i]];
    if (filter && (filter.values !== null || filter.condition)) filters++;
  }
  return {
    filters,
    sorts: state.sort.length,
    hiddenColumns: state.hiddenColumns.length,
    hiddenRows: state.hiddenRows.length,
    columnsReordered: state.columnOrder !== null,
  };
}

export function useSheetView(
  loaders: SheetLoader[],
  sourceKey: string,
): SheetViewApi {
  const rawCache = useRef<Record<string, RawSheet>>({});
  const defaultHeaders = useRef<Record<string, number | null>>({});

  const loadRaw = useCallback(
    (sheetId: string): RawSheet => {
      const cached = rawCache.current[sheetId];
      if (cached) return cached;
      const loader = loaders.find((item) => item.id === sheetId) ?? loaders[0];
      const raw = loader.load();
      rawCache.current[sheetId] = raw;
      if (!(sheetId in defaultHeaders.current)) {
        defaultHeaders.current[sheetId] = detectHeaderRow(raw.matrix);
      }
      return raw;
    },
    [loaders],
  );

  const [history, dispatch] = useReducer(viewHistoryReducer, loaders, (items) =>
    initialViewHistory({
      bySheet: {},
      activeSheetId: items.length > 0 ? items[0].id : "",
    }),
  );
  const workbook: WorkbookViewState = history.present;

  const [restored, setRestored] = useState<RestoredViewInfo | null>(null);
  const hydratedRef = useRef(false);

  const activeSheetId = workbook.activeSheetId;
  const raw = loadRaw(activeSheetId);
  const defaultHeaderRowIndex = defaultHeaders.current[activeSheetId] ?? null;

  const state: SheetViewState =
    workbook.bySheet[activeSheetId] ??
    emptySheetViewState(defaultHeaderRowIndex);

  // Seed the sheet's state with the detected header row, so that switching the
  // header off later stores a real null instead of falling back to the default.
  useEffect(() => {
    if (workbook.bySheet[activeSheetId]) return;
    dispatch({
      type: "ensureSheet",
      sheetId: activeSheetId,
      headerRowIndex: defaultHeaderRowIndex,
    });
  }, [workbook.bySheet, activeSheetId, defaultHeaderRowIndex]);

  // Restore a saved view once, and only for a file that has one.
  useEffect(() => {
    if (hydratedRef.current || !sourceKey) return;
    hydratedRef.current = true;
    const saved = loadSheetView(sourceKey);
    if (!saved || !saved.bySheet) return;
    const savedActive = saved.bySheet[saved.activeSheetId];
    if (!savedActive) return;
    dispatch({ type: "hydrate", state: saved });
    setRestored(describeRestored(savedActive));
  }, [sourceKey]);

  const persistable = useDebouncedValue(workbook, PERSIST_DEBOUNCE_MS);
  useEffect(() => {
    if (!sourceKey || !hydratedRef.current) return;
    const sheet = persistable.bySheet[persistable.activeSheetId];
    const modified =
      sheet != null &&
      isSheetViewWorthPersisting(
        sheet,
        defaultHeaders.current[persistable.activeSheetId] ?? null,
      );
    const anyModified =
      modified ||
      Object.keys(persistable.bySheet).some((id) =>
        isSheetViewWorthPersisting(
          persistable.bySheet[id],
          defaultHeaders.current[id] ?? null,
        ),
      );
    saveSheetView(
      sourceKey,
      anyModified
        ? persistable
        : { bySheet: {}, activeSheetId: persistable.activeSheetId },
    );
  }, [persistable, sourceKey]);

  const source = useMemo(
    () => buildSheetSource(raw, state.headerRowIndex),
    [raw, state.headerRowIndex],
  );

  const types = useMemo(() => detectColumnTypes(source), [source]);
  const widthBySourceIndex = useMemo(
    () => measureColumnWidths(source),
    [source],
  );

  const view = useMemo(
    () => buildView(source, state, types),
    [source, state, types],
  );

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const [anchor, setAnchor] = useState<MatchAnchor | null>(null);

  const { matches, truncated: matchesTruncated } = useMemo(
    () => findMatches(view, debouncedQuery),
    [view, debouncedQuery],
  );

  const activeMatchIndex = useMemo(
    () => resolveActiveIndex(matches, anchor),
    [matches, anchor],
  );
  const activeMatch = activeMatchIndex >= 0 ? matches[activeMatchIndex] : null;

  const matchesOutsideView = useMemo(() => {
    if (!debouncedQuery || matches.length > 0) {
      return { rows: 0, hiddenColumnRows: 0 };
    }
    return countMatchesOutsideView(source, view, debouncedQuery);
  }, [source, view, debouncedQuery, matches.length]);

  // A new query starts from the top; anything else keeps its anchor.
  useEffect(() => {
    setAnchor(null);
  }, [debouncedQuery, activeSheetId]);

  const gotoNextMatch = useCallback(() => {
    if (matches.length === 0) return;
    const next = (activeMatchIndex + 1 + matches.length) % matches.length;
    setAnchor(toAnchor(matches[next]));
  }, [matches, activeMatchIndex]);

  const gotoPrevMatch = useCallback(() => {
    if (matches.length === 0) return;
    const prev = (activeMatchIndex - 1 + matches.length) % matches.length;
    setAnchor(toAnchor(matches[prev]));
  }, [matches, activeMatchIndex]);

  const [selection, setSelection] = useState<GridSelection | null>(null);
  useEffect(() => {
    setSelection(null);
  }, [activeSheetId]);

  // Moving a column moves the data under a cell selection, and undo and redo
  // move columns too - which is why this watches the order rather than the drag
  // handler. The reducer hands back the same state object when nothing changed,
  // so this only fires on a real move, and a row selection survives it.
  useEffect(() => {
    setSelection(selectionAfterColumnsMoved);
  }, [state.columnOrder]);

  /**
   * Source and view for every sheet, for the all-sheets export. Sheets nobody
   * opened are built from an empty view state, so they export whole.
   */
  const buildAllSheetExports = useCallback((): SheetExportPart[] => {
    return loaders.map((loader) => {
      const rawSheet = loadRaw(loader.id);
      const sheetViewState =
        workbook.bySheet[loader.id] ??
        emptySheetViewState(defaultHeaders.current[loader.id] ?? null);
      const sheetSource = buildSheetSource(
        rawSheet,
        sheetViewState.headerRowIndex,
      );
      const sheetTypes = detectColumnTypes(sheetSource);
      return {
        source: sheetSource,
        view: buildView(sheetSource, sheetViewState, sheetTypes),
        label: loader.name,
        includeHeader: sheetViewState.headerRowIndex != null,
      };
    });
  }, [loaders, loadRaw, workbook.bySheet]);

  const setActiveSheet = useCallback((sheetId: string) => {
    dispatch({ type: "setActiveSheet", sheetId });
  }, []);

  const resetView = useCallback(() => {
    dispatch({
      type: "resetSheet",
      sheetId: activeSheetId,
      headerRowIndex: defaultHeaders.current[activeSheetId] ?? null,
    });
    setSelection(null);
    setRestored(null);
  }, [activeSheetId]);

  return {
    sheetIds: loaders.map((loader) => loader.id),
    sheetNames: loaders.map((loader) => loader.name),
    activeSheetId,
    setActiveSheet,

    raw,
    source,
    view,
    types,
    state,
    widthBySourceIndex,
    defaultHeaderRowIndex,
    isModified: isSheetViewModified(state, defaultHeaderRowIndex),

    query,
    setQuery,
    matches,
    matchesTruncated,
    activeMatchIndex,
    activeMatch,
    gotoNextMatch,
    gotoPrevMatch,
    matchesOutsideView,

    selection,
    setSelection,

    buildAllSheetExports,
    dispatch,
    undo: () => dispatch({ type: "historyUndo" }),
    redo: () => dispatch({ type: "historyRedo" }),
    canUndo: historyCanUndo(history),
    canRedo: historyCanRedo(history),
    restored,
    dismissRestored: () => setRestored(null),
    resetView,
  };
}
